import React, { useEffect, useRef, useState } from "react";
import "./PhysicalDesignApplyForm.css";

import schemaData from "./careerFormSchemas.json";
interface ApplicationField {
  name: string; label: string; type: string; required: boolean;
  options?: { value: string; text: string }[]; help?: string; trainingOnly?: boolean;
}

interface CaptchaApi {
  ready: (callback: () => void) => void;
  render: (element: HTMLElement, options: Record<string, unknown>) => number;
  execute: (id: number) => void;
  reset: (id: number) => void;
}
const CareerApplicationForm: React.FC<{ applicationUrl: string; jobTitle: string }> = ({ applicationUrl, jobTitle }) => {
  const schemaKey = (schemaData.urls as Record<string, string>)[applicationUrl] || "schema0";
  const fields = (schemaData.schemas as unknown as Record<string, ApplicationField[]>)[schemaKey];
  const [training, setTraining] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const getCaptcha = () => (window as unknown as { grecaptcha?: CaptchaApi }).grecaptcha;

  useEffect(() => {
    let active = true;
    const initialize = () => {
      const api = getCaptcha();
      if (!api) return;
      api.ready(() => {
        if (!active || !captchaRef.current || widgetRef.current !== null) return;
        widgetRef.current = api.render(captchaRef.current, {
          sitekey: "6LdyJVknAAAAAIYUox3eBpWUhCYIWfGHg-oLwyg-",
          size: "invisible",
          callback: (token: string) => {
            if (!active || !formRef.current) return;
            const response = formRef.current.elements.namedItem("g-recaptcha-response") as HTMLTextAreaElement | null;
            if (response) response.value = token;
            // Submit directly to the existing PHP handler, including the PDF.
            HTMLFormElement.prototype.submit.call(formRef.current);
          },
          "error-callback": () => {
            if (active) { setSending(false); setError("Verification failed. Please retry or use the official application form below."); }
          },
          "expired-callback": () => { if (active) setSending(false); },
        });
        setReady(true);
      });
    };
    let script = document.querySelector<HTMLScriptElement>('script[data-career-captcha]');
    if (!script) {
      script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.careerCaptcha = "true";
      document.head.appendChild(script);
    }
    script.addEventListener("load", initialize);
    const failed = () => { if (active) setError("Verification could not load. Please refresh or use the official application form below."); };
    script.addEventListener("error", failed);
    if (getCaptcha()?.render) initialize();
    return () => { active = false; script?.removeEventListener("load", initialize); script?.removeEventListener("error", failed); };
  }, []);

  return (
  <section className="pd-application mt-100" aria-labelledby="pd-application-title">
    <h3 id="pd-application-title">Apply for this job</h3>
    <p className="pd-application__intro">Apply for {jobTitle} at Digicomm Semiconductor.</p>
    <form ref={formRef} action="https://www.digicommsemi.com/assets/controllers/doEnq.php"
      method="post" encType="multipart/form-data" acceptCharset="UTF-8"
      onSubmit={(event) => {
        event.preventDefault();
        if (sending) return;
        if (!ready || widgetRef.current === null) { setError("Please wait for verification to load, or use the official application form below."); return; }
        setError(""); setSending(true);
        try { getCaptcha()?.execute(widgetRef.current); }
        catch { setSending(false); setError("Verification could not start. Please retry or use the official application form below."); }
      }}>
      <input type="hidden" name="position_applied_for" value={jobTitle} />
      <input type="hidden" name="enq_type" value="careers" />
      <div className="pd-application__grid">
        {fields.filter((field) => !field.trainingOnly || training === "1").map((field) => {
          const id = `application-${field.name}`;
          if (field.type === "radio") return (
            <fieldset className="pd-application__field pd-application__choices" key={field.name}>
              <legend>{field.label}{field.required ? "*" : ""}</legend>
              {field.help && <p className="pd-application__help">{field.help}</p>}
              {field.options?.map((option) => (
                <label key={option.value}><input type="radio" name={field.name} value={option.value}
                  required={field.required} onChange={field.name === "vlsi_sts_career" ? (event) => setTraining(event.target.value) : undefined} />{option.text}</label>
              ))}
            </fieldset>
          );
          return (
            <div className={`pd-application__field ${["file", "textarea"].includes(field.type) ? "pd-application__wide" : ""}`} key={field.name}>
              <label htmlFor={id}>{field.label}{field.required && <span aria-hidden="true">*</span>}</label>
              {field.type === "select" ? (
                <select id={id} name={field.name} required={field.required} defaultValue="">
                  <option value="" disabled>Select {field.label.toLowerCase()}</option>
                  {field.options?.map((option, index) => <option value={option.value} key={`${option.value}-${index}`}>{option.text}</option>)}
                </select>
              ) : field.type === "textarea" ? (
                <textarea id={id} name={field.name} required={field.required} rows={6} />
              ) : (
                <input id={id} name={field.name} type={field.name === "contact_career" ? "tel" : field.type}
                  required={field.required} min={field.type === "number" ? 0 : undefined}
                  step={field.type === "number" ? "0.01" : undefined}
                  pattern={field.name === "contact_career" ? "[0-9]{10}" : undefined}
                  title={field.name === "contact_career" ? "Enter 10 digit mobile number" : undefined}
                  accept={field.type === "file" ? ".pdf,application/pdf" : undefined}
                  onChange={field.type === "file" ? (event) => {
                    const file = event.target.files?.[0];
                    event.target.setCustomValidity(file && !file.name.toLowerCase().endsWith(".pdf") ? "Please upload your resume as a PDF." : "");
                    event.target.reportValidity();
                  } : undefined} />
              )}
            </div>
          );
        })}
      </div>
      <p className="pd-application__status">Upload your resume as a PDF. Your application will be sent securely to Digicomm Semiconductor.</p>
      <div ref={captchaRef} />
      {error && <p role="alert" className="pd-application__error">{error}</p>}
      <button className="pd-application__submit" type="submit" disabled={!ready || sending}>{sending ? "Verifying application..." : "Submit application"}</button>
      <a className="pd-application__official" href={applicationUrl} target="_blank" rel="noopener noreferrer">Open official application form</a>
    </form>
  </section>
  );
};
export default CareerApplicationForm;
